/**
 * seatRoutePolicy — unified role → route policy map for all workspace seats.
 *
 * Replaces the attorney-only deny-list with one policy per role.
 * Every policy is a deny-list (blockedPrefixes) so unmapped routes stay open
 * by default — we block explicitly, never lock out by omission.
 *
 * The `client` seat is the ONLY allow-list seat: when `allowedPrefixes` is set,
 * the path is blocked unless it matches one of the prefixes. This is defense in
 * depth — `/client/*` and `/portal/*` are already public routes, but the guard
 * ensures a signed-in client cannot wander onto staff surfaces.
 *
 * `/attorney/*` and `/workdesk/attorney` are intentionally NOT listed for staff
 * seats: those routes sit behind ProtectedAttorneyRoute (not ProtectedRoute), which
 * deliberately admits company staff for cross-firm oversight
 * (AttorneyAuthContext.tsx), so SeatRouteGuard never runs there. Changing that is an
 * owner decision, not a map edit.
 */

import type { WorkspaceRole } from '../../config/toolsManifest';

export type SeatRole = WorkspaceRole;

export interface SeatPolicy {
  home: string;
  blockedPrefixes: string[];
  allowExceptions?: string[];
  /** Allow-list: when present, the path is blocked UNLESS it matches one of these prefixes.
   *  Segment-exact rule applies (`/clientx` does NOT match `/client`).
   *  Only the `client` seat uses this today. */
  allowedPrefixes?: string[];
}

export const SEAT_ROUTE_POLICY: Record<SeatRole, SeatPolicy> = {
  attorney: {
    home: '/attorney/workdesk',
    blockedPrefixes: ['/dashboard', '/queue', '/adjuster', '/data-specialist'],
    allowExceptions: ['/queue/attorney'],
  },
  data_specialist: {
    home: '/workdesk/data-specialist',
    blockedPrefixes: [
      '/dashboard/accountant',
      '/workdesk/accountant-internal',
      '/workdesk/accountant-external',
      '/adjuster',
    ],
  },
  accountant_internal: {
    home: '/workdesk/accountant-internal',
    blockedPrefixes: [
      '/adjuster',
      '/data-specialist',
      '/workdesk/data-specialist',
    ],
  },
  admin: { home: '/workdesk/admin', blockedPrefixes: [] },
  analyst: { home: '/workdesk/analyst', blockedPrefixes: [] },
  attorney_internal: { home: '/attorney/workdesk', blockedPrefixes: [] },
  mediator: { home: '/mediation-wall', blockedPrefixes: [] },
  client: { home: '/client', blockedPrefixes: [], allowedPrefixes: ['/client', '/portal'] },
  accountant_external: { home: '/workdesk/accountant-external', blockedPrefixes: [] },
  // PR1 placeholder: no seat shell yet (PR5 sets the real home); deny every staff/attorney/mediator shell.
  public_adjuster: { home: '/', blockedPrefixes: ['/dashboard', '/queue', '/adjuster', '/data-specialist', '/workdesk', '/attorney', '/mediation-wall', '/admin'] },
  carrier_adjuster: { home: '/', blockedPrefixes: ['/dashboard', '/queue', '/adjuster', '/data-specialist', '/workdesk', '/attorney', '/mediation-wall', '/admin'] },
};

/**
 * Return true when `pathname` is blocked for the given seat role.
 *
 * Matching is segment-exact: `/dashboardx` is NOT blocked by `/dashboard`.
 * Query strings and trailing slashes are ignored.
 */
export function isPathBlocked(role: SeatRole, pathname: string): boolean {
  const policy = SEAT_ROUTE_POLICY[role];
  if (!policy) {
    return false;
  }

  const path = pathname.split('?')[0];
  const normalized =
    path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  // Allow-list seat: blocked unless path matches an allowed prefix.
  if (policy.allowedPrefixes && policy.allowedPrefixes.length > 0) {
    for (const prefix of policy.allowedPrefixes) {
      if (normalized === prefix || normalized.startsWith(prefix + '/')) {
        return false;
      }
    }
    return true;
  }

  if (policy.blockedPrefixes.length === 0) {
    return false;
  }

  for (const prefix of policy.blockedPrefixes) {
    if (normalized === prefix || normalized.startsWith(prefix + '/')) {
      if (policy.allowExceptions) {
        for (const exc of policy.allowExceptions) {
          if (normalized === exc || normalized.startsWith(exc + '/')) {
            return false;
          }
        }
      }
      return true;
    }
  }
  return false;
}

/**
 * Backward-compatible wrapper — the old attorney-only guard.
 * Existing imports and tests continue to work unchanged.
 */
export function isStaffOnlyPathForAttorney(pathname: string): boolean {
  return isPathBlocked('attorney', pathname);
}
